import PatientNextVisitService, {
  TNextVisitMarkType,
  type IPatientNextVisitFilter,
  type TPatientNextVisit,
} from "@/api/models/patient/nextVisit";
import {
  useGenericListQuery,
  useGenericQuery,
  createQueryKeys,
  type TItemQueryBehaviorOptions,
} from "@/composables/shared/queries";
import type { TItemQueryComposableParams } from "@/composables/shared/filters/useGeneralFilter";
import { type MaybeRefOrGetter } from "vue";
import { useGenericMutation } from "@/composables/shared/mutations/useGenericMutation";

export const NextVisitQueryKeys = createQueryKeys(PatientNextVisitService.queryKey, (key) => ({
  listOfNextVisits: key<IPatientNextVisitFilter>("list"),
  detail: key<string>("detail"),
}));

export const usePatientNextVisits = (
  filters: MaybeRefOrGetter<Partial<IPatientNextVisitFilter>>,
  enabled: MaybeRefOrGetter<boolean> = true
) => {
  return useGenericListQuery<TPatientNextVisit, Partial<IPatientNextVisitFilter>>({
    queryKey: NextVisitQueryKeys.listOfNextVisits(),
    params: { filters, enabled },
    fetcher: (f) => PatientNextVisitService.getAll({}, f),
    permission: $permSubjects.PATIENT__INSTRUCTIONFORNEXTVISIT,
    neededKey: ["patient"],
  });
};

// export const useNextVisitOfPatient = (patientId: MaybeRef<string>) => {
//   const refPatientId = toRef(patientId);
//   const { can } = useAbility();
//   return useQuery({
//     queryKey: computed(() => ["nextVisitOfPatient", refPatientId.value]),
//     queryFn: async (): Promise<TPatientNextVisit | null> => {
//       const response = await PatientNextVisitService.getAll(refPatientId.value);
//       return response.data.at(0) ?? null;
//     },
//     enabled: computed(() => can($permActions.READ, $permSubjects.PATIENT__INSTRUCTIONFORNEXTVISIT)),
//   });
// };

export const useNextVisit = (
  params: TItemQueryComposableParams<string>,
  options: TItemQueryBehaviorOptions = {}
) => {
  return useGenericQuery<TPatientNextVisit, string>({
    queryKey: NextVisitQueryKeys.detail(),
    params,
    fetcher: (id) => PatientNextVisitService.get({ id }),
    permission: $permSubjects.PATIENT__INSTRUCTIONFORNEXTVISIT,
    ...options,
  });
};

export function useCreatePatientNextVisit(veeSetErrors?: any) {
  return useGenericMutation<TPatientNextVisit, Partial<TPatientNextVisit>>({
    mutationFn: (nextVisit) => PatientNextVisitService.create({}, nextVisit as TPatientNextVisit),
    feature: "patientNextVisit-create",
    params: { veeSetErrors },
  });
}

export function useUpdatePatientNextVisit(veeSetErrors?: any) {
  return useGenericMutation<TPatientNextVisit, Partial<TPatientNextVisit> & { id: string }>({
    mutationFn: (nextVisit) => PatientNextVisitService.update({ id: nextVisit.id }, nextVisit),
    feature: "patientNextVisit-update",
    params: { veeSetErrors },
  });
}

export function usePatientNextVisitConfirm(veeSetErrors?: any) {
  return useGenericMutation<TPatientNextVisit, string>({
    mutationFn: (nextVisitId) =>
      PatientNextVisitService.markAs(
        { nextVisit: nextVisitId, type: TNextVisitMarkType.CONFIRM },
        {}
      ),
    feature: "patientNextVisit-confirm",
    params: { veeSetErrors },
  });
}

export function usePatientNextVisitInstructionsMarkAs() {
  return useGenericMutation<TPatientNextVisit, { nextVisitId: string; type: TNextVisitMarkType }>({
    mutationFn: ({ nextVisitId, type }) =>
      PatientNextVisitService.markAs({ nextVisit: nextVisitId, type }, {}),
    feature: "patientNextVisit-markAs",
  });
}
